import { useState, useRef, useEffect } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";
import { customerSearch, verifyReturn, syncSingleProductByValue } from "../../../services";
import db from "../../../database";
import messages from "../../../errors/messages.en-IN.json";
import useTransactionStore from "../../../stores/useTransactionStore";
import useAuthStore from "../../../stores/useAuthStore";
import { v4 as uuidv4 } from "uuid";

/**
 * Custom hook encapsulating all sales-return logic extracted from PointOfSaleRetail.
 */
const useSalesReturn = ({ cart, setCart, addProduct, posLogActivity, setDisplayReturnOrderSearch, setKeyValue, setProductsList, productsList, loader }) => {
  const { t } = useTranslation();
  const tillData = useAuthStore((s) => s.tillData);

  const [showReturnPurchase, setShowReturnPurchase] = useState(false);
  const [retrunSearchType, setReturnSearchType] = useState("orderNo");
  const [selectedReturnOrder, setSelectedReturnOrder] = useState({});
  const [modalEditState, setModalEditState] = useState(false);
  const [returnProcessLoading, setReturnProcessLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const returnBillSearchInputRef = useRef();
  const returnProcessRef = useRef();

  useEffect(() => {
    if (!useTransactionStore.getState().salesReturn) {
      useTransactionStore.getState().setSalesReturn("N");
    }
  }, []);

  // Focus the search input whenever the search panel becomes visible or search type changes
  useEffect(() => {
    // displayReturnOrderSearch is managed by the parent; we expose returnBillSearchInputRef
    // so the parent can trigger focus.  The actual focus call stays in the parent useEffect
    // that watches displayReturnOrderSearch / retrunSearchType.
  }, [retrunSearchType]);

  const columns1 = [
    {
      title: "Description",
      dataIndex: "product",
      width: "43%",
      render: (_, record) => record.product.name,
    },
    {
      title: "Unit Price",
      dataIndex: "grossstd",
      render: (_, record) => parseFloat(record.grossstd).toFixed(2),
    },
    {
      title: "Qty Ordered",
      dataIndex: "qty",
    },
    {
      title: "Qty to Return",
      dataIndex: "qtyToReturn",
    },
  ];

  let selectIndex = 0;
  const selectPurchaseBillForReturn = (data) => {
    if (selectIndex === 0) {
      let returnBillItem = data[0];
      setDisplayReturnOrderSearch(false);
      setReturnSearchType("orderNo");
      const discountValue = returnBillItem.discAmount;
      let grossstdTotal = 0;
      for (let i = 0; i < returnBillItem.line.length; i += 1) {
        grossstdTotal += parseFloat(returnBillItem.line[i].grossstd);
      }
      const zeroDiscountCount = returnBillItem.line.filter((item) => item.discount === 0).length;
      if (zeroDiscountCount === returnBillItem.line.length && discountValue > 0) {
        returnBillItem.line.forEach((addedToCart, index) => {
          const discountAmt = parseFloat((addedToCart.grossstd / grossstdTotal) * discountValue) / addedToCart.qty;
          const sp = parseFloat(addedToCart.grossstd) - discountAmt;
          addedToCart.grossstd = sp;
          addedToCart.linestd = sp * addedToCart?.weight;
          returnBillItem.line[index] = addedToCart;
        });
      }
      const updatedDataSource = [...returnBillItem.line];
      updatedDataSource.forEach((_, i) => {
        updatedDataSource[i].key = `${i}`;
        updatedDataSource[i].qtyToReturn = updatedDataSource[i].qtyToReturn ? updatedDataSource[i].qtyToReturn : 0;
      });
      updatedDataSource.itemsToReturn = 0;
      updatedDataSource.itemsQtyToReturn = 0;
      updatedDataSource.returnAmountTotal = 0;
      setDataSource([...updatedDataSource]);
      setSelectedReturnOrder({ ...returnBillItem });
      selectIndex++;
    }
  };

  const searchReturnHistory = async (documentno) => {
    const searchQuery = `${retrunSearchType} : "${documentno}"`;
    const authHeaders = getOAuthHeaders();
    if (authHeaders && authHeaders.access_token) {
      const data = {
        query: `query {
                    verifyReturn(${searchQuery}) {
                        sOrderID
                        created
                        createdby
                        updated
                        updatedby
                        documentno
                        salesRepID
                        dateordered
                        totalQty
                        orderTime
                        cwrProductQty
                        taxamt
                        grosstotal
                        discAmount
                        csBUnit {
                            csBunitId
                            name
                        }
                        cwrB2cCustomer {
                            cwrCustomerId
                            code
                            name
                            mobileNo
                            pincode
                            email
                            retlLoyaltyBalance
                            sCustomer {
                                sCustomerID
                                customerCategory {
                                    sCustomerCateforyId
                                    value
                                    name
                                    description
                                }
                            }
                            metaData {
                                key
                                value
                            }
                        }
                        saleType {
                            cwrSaletypeId
                            name
                            value
                        }
                        cwrTill {
                            cwrTillID
                            till
                        }
                        finReceiptPlan {
                            finReceiptPlanDetails {
                                amount
                                cwrPaymentmethod {
                                    cWRPaymentMethodID
                                    finFinancialAccountId
                                    finPaymentmethodId
                                    integratedPayment
                                    isloyalty
                                    paymentProvider
                                }
                            }
                        }
                        line {
                            sOrderlineID
                            sOrderId
                            line
                            description
                            qty
                            netlist
                            netunit
                            linetax
                            unittax
                            linenet
                            linegross
                            grosslist
                            grossstd
                            returnline
                            returnQty
                            discount
                            mBatchId
                            batchNo
                            product {
                                mProductId
                                name
                                value
                                upc
                                hsncode
                                imageurl
                                isManualQty
                                shortDescription
                                returnable
                                returnDays
                            }
                            uom {
                                csUomId
                                name
                            }
                            tax {
                                csTaxID
                                name
                                rate
                            }
                            pricingRule {
                                mPricingrulesId
                                name
                            }
                        }
                    }
                }`,
      };
      try {
        const searchData = await verifyReturn(data);
        selectPurchaseBillForReturn(searchData.data.data.verifyReturn);
        setShowReturnPurchase(true);
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
      }
    }
  };

  const handleSaveReturnCartRecord = (data) => {
    let itemsToReturn = 0;
    let itemsQtyToReturn = 0;
    let returnAmountTotal = 0;
    data.forEach((item) => {
      if (item.qtyToReturn > 0) {
        itemsToReturn += 1;
        itemsQtyToReturn += item.qtyToReturn;
        returnAmountTotal += item.qtyToReturn * item.grossstd;
      }
    });
    selectedReturnOrder.line = data;
    selectedReturnOrder.itemsToReturn = itemsToReturn;
    selectedReturnOrder.itemsQtyToReturn = itemsQtyToReturn;
    selectedReturnOrder.returnAmountTotal = returnAmountTotal;
    setSelectedReturnOrder({ ...selectedReturnOrder });
  };

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setDataSource(newData);
    handleSaveReturnCartRecord(newData);
    setTimeout(() => {
      returnProcessRef.current.focus();
    }, 750);
  };

  const getCustomerData = async (mobileNo) => {
    const authHeaders = getOAuthHeaders();
    if (authHeaders && authHeaders.access_token) {
      const data = {
        query: `query{
            customerSearch(mobileNo:"${mobileNo}"){
              cwrCustomerId
              code
              name
              email
              mobileNo
              name
              pincode
              retlLoyaltyBalance
              b2cRegisteredstoreId
               iscredit
              balancepoints
              loyaltyLevel{
                  cwrLoyaltyLevelId
                  name
                  accumulationRate
                  redemptionRate
          }
          cwrWallet {
               wallet_Id
              balance_Amt
          }
              sCustomer{
                sCustomerID
                customerCategory{
                  sCustomerCateforyId
                  value
                  name
                  description
                }
              }
              csBunit{
                csBunitId
                name
              }
              b2cCustomerSegment{
                name
                cwrB2CCustomerSegmentId
              }
            }
          }`,
      };
      try {
        const response = await customerSearch(data);
        if (response.data.data.customerSearch !== null && response.data.data.customerSearch.length > 0) {
          // result available but not used in original code
        } else {
          console.warn("No customer found, try searching with different customer");
        }
      } catch (err) {
      }
    }
  };

  const getProductData = (productId, productValue) => {
    return new Promise(function (resolve) {
      db.products
        .where("mProductId")
        .equalsIgnoreCase(productId)
        .toArray()
        .then((allProducts) => allProducts.filter((p) => p.isactive == true && p.productSegment !== "BG"))
        .then(async (product) => {
          if (product.length === 0 && productValue) {
            try {
              const syncId = uuidv4().replace(/-/g, "").toUpperCase();
              const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
              await syncSingleProductByValue(tillData, syncId, tillId, productValue, { ignoreLastSync: true });
              const refetched = await db.products.where("mProductId").equalsIgnoreCase(productId).toArray();
              product = refetched.filter((p) => p.isactive == true && p.productSegment !== "BG");
            } catch (err) {
              console.error("syncSingleProductByValue fallback failed:", err);
            }
          }
          if (product.length > 0) {
            const obj = { ...product[0] };
            if (obj.overRideTax === "Y" && obj.sunitprice <= obj.overRideCondition) {
              const originalPrice = obj.sunitprice - (obj.sunitprice - obj.sunitprice * (100 / (100 + obj.taxRate)));
              const taxedPrice = originalPrice + (originalPrice * obj.contraRate) / 100;
              obj.sunitprice = taxedPrice;
              obj.cTaxId = obj.contraTaxId;
              obj.taxRate = obj.contraRate;
            }
            const productDefined = {
              batchno: null,
              description: obj.description,
              discount: 0,
              discountName: "",
              imageurl: obj.imageurl,
              isDecimal: obj.isDecimal,
              isManualQty: obj.isManualQty,
              restrictPosQtyEdit: obj.restrictPosQtyEdit || "N",
              showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
              interTaxId: obj?.interTaxId || "",
              interTaxRate: obj?.interTaxRate || 0,
              interTaxName: obj?.interTaxName || "",
              alternateUom: obj?.alternateUom || null,
              isPromoApplicable: false,
              isReturn: true,
              mBatchId: null,
              mPricingruleId: null,
              name: obj.name,
              name2: obj.name2,
              nettotal: 0,
              primaryOrderLine: null,
              productId: obj.mProductId,
              realPrice: obj.sunitprice,
              productSegment: obj?.productSegment || "",
              returnQty: null,
              salePrice: obj.sunitprice,
              mrpPrice: obj.sunitprice,
              stock: obj.onhandQty,
              taxCategory: obj.taxCategory,
              taxName: obj.taxName,
              taxFlag: obj?.taxFlag || "N",
              tax: obj.cTaxId,
              taxAmount: 0,
              taxRate: obj.taxRate,
              uom: obj.csUomId,
              uom_name: obj.uomName,
              upc: obj.upc,
              value: obj.value,
              weight: 0,
              shortDescription: obj.shortDescription,
              hsncode: obj.hsncode,
              csBunitId: obj.csBunitId,
              mProductCategoryId: obj.mProductCategoryId,
              productManufacturerId: obj.productManufacturerId,
              productBrandId: obj.productBrandId,
              productBrandName: obj?.productBrandName,
              batchedProduct: obj.batchedProduct,
              batchedForSale: obj.batchedForSale,
              batchedForStock: obj.batchedForStock,
              multiPrice: obj.multiPrice,
              shelfLife: obj.shelfLife,
            };
            resolve(productDefined);
          } else {
            message.warning(`${t("product_search_error")}`);
            resolve(null);
          }
        });
    });
  };

  const addSelectedReturnProductsToCart = async () => {
    const customerResult = await getCustomerData(selectedReturnOrder.cwrB2cCustomer.mobileNo);
    if (!customerResult) {
      setCart({
        ...cart,
        customer: customerResult === null ? cart.customer : customerResult,
      });
    } else {
      setCart({
        ...cart,
        customer: selectedReturnOrder.cwrB2cCustomer,
      });
    }

    for (let index = 0; index < selectedReturnOrder.line.length; index++) {
      const item = { ...selectedReturnOrder.line[index] };
      const productItem = await getProductData(item.product.mProductId, item.product.value);
      if (productItem) {
        productItem.realPrice = item.grossstd;
        productItem.salePrice = item.grossstd;
        productItem.mrpPrice = item.grossstd;
        productItem.sOrderReturnId = item.sOrderId;
        productItem.sOrderlineReturnId = item.sOrderlineID;
        productItem.batchno = item?.batchNo?.split("-:")[0] || item?.batchNo || null;
        productItem.mBatchId = item?.mBatchId || null;
        addProduct(productItem, -item.qtyToReturn);
        if (
          db.logConfiguration.toArray().then((fetched) => {
            fetched.map((logItem) => {
              logItem.salesReturn.filter((logItem) => logItem.log === "Y").length > 0;
            });
          })
        ) {
          posLogActivity(productItem, "SLR");
        }
        await new Promise((r) => setTimeout(r, 900));
      }
    }
    setKeyValue("0");
    setReturnProcessLoading(false);
  };

  return {
    // state
    showReturnPurchase,
    setShowReturnPurchase,
    retrunSearchType,
    setReturnSearchType,
    selectedReturnOrder,
    setSelectedReturnOrder,
    modalEditState,
    setModalEditState,
    returnProcessLoading,
    setReturnProcessLoading,
    dataSource,
    setDataSource,
    // refs
    returnBillSearchInputRef,
    returnProcessRef,
    // table columns
    columns1,
    // handlers
    searchReturnHistory,
    handleSave,
    addSelectedReturnProductsToCart,
  };
};

export default useSalesReturn;
